<template>
  <section>
    <one-line-form :submit="update" :submittable="!!answer && isValid" :saving="isSaving ? answer : ''">
          <input-field name="amount"
                        :class="{ invisible: isSaving }"
                        type="text"
                        :title="screenParams.text()"
                        :placeholder="screenParams.placeholder()"
                        :autocapitalize="true"
                        :autocorrect="false"
                        :value="answer"
                        @input="updateAnswer"
                        v-keyboard="'tel'" />
    </one-line-form>
    <helper v-if="answer && screenParams.validation && !isValid && !isSaving" :error="true" class="warningHint">{{ warningHint }}</helper>
    <helper v-if="isValid && screenParams.description && !isSaving">{{ screenParams.description() }}</helper>
    <helper v-if="screenParams.dynamicHint && !isValid && !isSaving">{{ screenParams.dynamicHint($store) }}</helper>
    <helper v-show="screenParams.changeCurrency && !isSaving" :questions="true" :class="{ hint: true, android: $store.state.app.deviceIsAndroid}"><a @click="toggleModal">{{ i18n.t('currency:other') }}</a></helper>

    <modal :title="i18n.t('currency:currency')" v-if="showCurrencyModal" @closeModal="toggleModal" :highHeader="true">
      <currency-selector slot="body" :screenParams="screenParams.currencySettings" :currency="true" :onSelect="changeCurrency" ></currency-selector>
    </modal>
    <show-keyboard></show-keyboard>
  </section>
</template>

<style lang="scss" rel="stylesheet/scss" scoped>
  @import '../assets/mixins';
  ul {
    @include button-list;
  }
  input {
    &::-webkit-input-placeholder {
      font-weight: 400;
    }
    &[disabled] {
      color: black;
    }
    border-bottom-color: transparent;
  }
  .warningHint {
    margin-bottom: 10px;
  }
</style>

<style lang="scss" rel="stylesheet/scss" scoped>
  @import '../assets/_settings';
  @import '../assets/mixins';

  .title {
    position: absolute;
    left:0;
    margin-top: -30px;
    font-weight: 700;
    font-size: 1.375rem;
    color: #515151;
    width: 100%;
    text-transform: capitalize;
    @include tablet {
      width: $tablet-content-width;
      margin: -30px auto auto;
      right: 0;
    }
  }
  .hint {
    text-align: right;
    margin-top: 50px;

    a {
      font-size: $inputLinkSize;
      font-size: $inputLinkSize-portal;
      color: $inputLinkColor;
      color: $inputLinkColor-portal;
      font-weight: $inputLinkWeight;
      font-weight: $inputLinkWeight-portal;
      text-decoration: underline;
      cursor: pointer;
    }
  }
  .android {
    padding-bottom: 0;

    a {
      padding-top: 20px;
    }
  }
</style>

<script>
  import Vue from 'vue'
  import { getNestedProperty } from '../utils/structure.js'
  import currencies from '../components/Currencies'

  import { format, unformat, setCursor, event } from '../utils/money-formatter'

  export default Vue.component('amount', {
    components: {
      'currency-selector': currencies
    },
    props: [
      'screenParams'
    ],
    data () {
      const key = this.screenParams && this.screenParams.dispatchKey ? this.screenParams.dispatchKey.replace('update', '').toLowerCase() : 'user'
      const propertyName = (this.screenParams && this.screenParams.propertyName) || 'personalNumber'
      const currentValue = getNestedProperty(this.$store.state[key], propertyName)
      const currency = (this.screenParams.changeCurrency && this.$store.state.user.currency && ` ${this.$store.state.user.currency}`) || '€'
      const formatterOpt = { prefix: '', suffix: currency, thousands: ' ', decimal: '', precision: 0 }
      const min = this.screenParams.validation.min && this.screenParams.validation.min()
      const max = this.screenParams.validation.max && this.screenParams.validation.max()
      return {
        answer: currentValue || '',
        currency: currency,
        isSaving: false,
        propertyName: propertyName,
        dispatchKey: (this.screenParams && this.screenParams.dispatchKey) || 'updateUser',
        formattedValue: '',
        showCurrencyModal: false,
        formatterOpt,
        min,
        max
      }
    },
    computed: {
      isValid () {
        if (!this.screenParams.validation) {
          return true
        } else if (this.screenParams.validation && this.answer) {
          const amount = unformat(this.answer, this.formatterOpt.precision)
          const validMin = this.min ? amount >= this.min : true
          const validMax = this.max ? amount <= this.max : true
          return validMax && validMin
        } else {
          return false
        }
      },
      warningHint () {
        const currency = this.currency
        if (this.min && this.max) {
          return this.i18n.t('amount:amount_between_hint', {min: this.min, max: this.max, currency: currency})
        } else if (this.min) {
          return this.i18n.t('amount:amount_min_hint', {min: this.min, currency: currency})
        } else if (this.max) {
          return this.i18n.t('amount:amount_max_hint', {max: this.max, currency: currency, type: (this.screenParams.validation.inputType && this.screenParams.validation.inputType()) || 'input'})
        }
      }
    },
    methods: {
      updateAnswer (value) {
        const input = document.querySelector('input[name=amount]')

        var positionFromEnd = input.value.length - input.selectionEnd
        input.value = format(input.value, this.formatterOpt)
        positionFromEnd = Math.max(positionFromEnd, this.formatterOpt.suffix.length) // right
        positionFromEnd = input.value.length - positionFromEnd
        positionFromEnd = Math.max(positionFromEnd, this.formatterOpt.prefix.length + 1) // left
        setCursor(input, positionFromEnd)
        this.answer = input.value.toString()
        input.dispatchEvent(event('change'))
      },
      update () {
        if (this.answer) {
          this.isSaving = true

          let dispatchData = {}
          dispatchData[this.propertyName] = unformat(this.answer, this.formatterOpt.precision).toString()
          this.$store.dispatch(this.dispatchKey, dispatchData)

          this.$store.dispatch('next', this.screenParams.nextScreenRoute)
        }
      },
      toggleModal () {
        this.showCurrencyModal = !this.showCurrencyModal
      },
      changeCurrency (data) {
        const currency = data && data.currency
        if (currency) {
          this.currency = data.currency
          this.formatterOpt.suffix = ` ${data.currency}`
        }

        this.showCurrencyModal = false
        const input = this.$el.querySelector('input[name=amount]')
        if (input) {
          input.focus()
          this.updateAnswer(this.answer)
        }
      }
    }
  })
</script>


// WEBPACK FOOTER //
// src/screens/Amount.vue